/// Leaderboard overlay shown on top of the level-detail modal.
///
/// Tabbed by difficulty (Novice / Trainee / Licensed / Master).
/// Fetches the top 50 entries from Firestore on mount + on tab
/// change. The current user's row (if present) is highlighted.

import { useEffect, useMemo, useState } from 'react'
import {
  fetchLeaderboard,
  type LeaderboardEntry,
} from '../data/leaderboard'
import {
  DIFFICULTY_ORDER,
  type DifficultyName,
} from '../data/userProfile'

export function LeaderboardPanel(props: {
  levelId: string
  levelDisplayName: string
  initialDifficulty: DifficultyName
  currentUid: string | null
  onClose: () => void
}) {
  const [difficulty, setDifficulty] = useState<DifficultyName>(props.initialDifficulty)
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setEntries(null)
    setError(null)
    fetchLeaderboard({ levelId: props.levelId, difficulty })
      .then((data) => {
        if (!cancelled) setEntries(data)
      })
      .catch((e) => {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e)
          setError(msg)
          setEntries([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [props.levelId, difficulty])

  return (
    <div
      className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={props.onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-md flex-col rounded-xl bg-slate-900 p-5 text-slate-100 shadow-2xl ring-1 ring-white/10"
      >
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold tracking-wide">
            {props.levelDisplayName}
            <span className="ml-2 text-xs font-normal text-slate-400">
              · Leaderboard
            </span>
          </h3>
          <button
            onClick={props.onClose}
            className="text-xs text-slate-400 hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="mt-3 flex gap-1.5">
          {DIFFICULTY_ORDER.map((d) => (
            <button
              key={d}
              onClick={() => setDifficulty(d)}
              className={
                'flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ' +
                (d === difficulty
                  ? 'bg-emerald-600 text-white shadow ring-1 ring-emerald-400'
                  : 'bg-slate-800 text-slate-300 ring-1 ring-white/10 hover:bg-slate-700')
              }
            >
              {d}
            </button>
          ))}
        </div>

        <LeaderboardList
          entries={entries}
          error={error}
          currentUid={props.currentUid}
        />
      </div>
    </div>
  )
}

function LeaderboardList(props: {
  entries: LeaderboardEntry[] | null
  error: string | null
  currentUid: string | null
}) {
  const myRow = useMemo(() => {
    if (!props.entries || !props.currentUid) return -1
    return props.entries.findIndex((e) => e.uid === props.currentUid)
  }, [props.entries, props.currentUid])

  if (props.error) {
    return (
      <div className="mt-4 rounded-md bg-rose-900/40 px-3 py-3 text-sm text-rose-200 ring-1 ring-rose-500/30">
        Couldn't load leaderboard: {props.error}
      </div>
    )
  }
  if (props.entries === null) {
    return (
      <div className="mt-4 flex h-32 items-center justify-center text-sm text-slate-400">
        Loading…
      </div>
    )
  }
  if (props.entries.length === 0) {
    return (
      <div className="mt-4 flex h-32 items-center justify-center text-sm text-slate-400">
        No scores yet — be the first.
      </div>
    )
  }

  return (
    <div className="mt-3 overflow-y-auto rounded-md bg-slate-800/40 ring-1 ring-white/5">
      <table className="w-full text-sm">
        <tbody>
          {props.entries.map((entry, idx) => {
            const isMe = idx === myRow
            return (
              <tr
                key={entry.uid + entry.difficulty}
                className={
                  'border-b border-white/5 last:border-0 ' +
                  (isMe ? 'bg-emerald-900/30' : '')
                }
              >
                <td className="w-10 px-3 py-1.5 text-right font-mono text-xs text-slate-400">
                  {idx + 1}
                </td>
                <td className="truncate px-2 py-1.5">
                  <span className={isMe ? 'font-semibold text-emerald-200' : ''}>
                    {entry.displayName}
                  </span>
                  {isMe && (
                    <span className="ml-1.5 text-[10px] uppercase tracking-wider text-emerald-300">
                      you
                    </span>
                  )}
                </td>
                <td className="w-20 px-3 py-1.5 text-right font-mono">
                  {entry.score.toLocaleString()}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
